import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { PauseCircle } from 'phosphor-react';
import exact from 'prop-types-exact';
import { PlayCircle, Prohibit } from '@phosphor-icons/react';
import PlaceholderImage from '../../../common/placeholderImage/PlaceholderImage';
import Button from '../../../common/button/Button';
import DownloadButton from '../../../common/DownloadButton/DownloadButton';

const IMG_MEDIA_URL = 'https://a1media.antena1.com.br/image/album/200x200/';
const DEFAULT_ALBUM = `${IMG_MEDIA_URL}defaultAlbum.jpg`;

const MusicItem = ({
    currentMusicId, musicIndex, playMediaPlayer, pauseMediaPlayer,
    setPlaylist, playerStatus, playlistType, isLive,
    useFilteredMusics, playlistData, filteredMusics, music, showDownloadButton,
}) => {
    const isCurrentMusic = useMemo(() => (
        currentMusicId === music.musicId && isLive === 'otherMedia'
    ), [currentMusicId, music, isLive]);

    const handlingPause = useCallback(() => {
        pauseMediaPlayer();
    }, [pauseMediaPlayer]);

    const setPlaylistHandler = useCallback(() => {
        const itemMusicId = music.musicId;
        const treatedPlaylist = [];
        let musicIndexInPlaylist = 0;
        let numItems = 0;
        const playlistToBeSet = !useFilteredMusics ? playlistData : filteredMusics;

        playlistToBeSet.forEach((track) => {
            if (track.musicTrack) {
                treatedPlaylist.push(track);

                numItems += 1;

                if (track.musicId === itemMusicId) {
                    musicIndexInPlaylist = numItems - 1;
                }
            }
        });

        setPlaylist(playlistType, treatedPlaylist, musicIndexInPlaylist, itemMusicId);
    }, [music, playlistData, filteredMusics, useFilteredMusics, setPlaylist, playlistType]);

    const handlingPlay = useCallback(() => {
        if (isCurrentMusic && playerStatus === 'paused') {
            playMediaPlayer();
        }

        setPlaylistHandler();
    }, [isCurrentMusic, playerStatus, playMediaPlayer, setPlaylistHandler]);

    const {
        musicName, musicSlug, musicTrack, albumName, artistName,
        artistSlug, albumCover, itunesCover,
    } = music;

    const imageSrc = albumCover ? IMG_MEDIA_URL + albumCover : (itunesCover || DEFAULT_ALBUM);
    const play = isCurrentMusic && playerStatus === 'playing';

    return (
        <article className="tw-flex tw-justify-start tw-gap-8">
            <div className="tw-flex tw-items-start tw-flex-col tw-gap-2 itemListTop10">
                <div className="tw-relative">
                    <figure>
                        <PlaceholderImage placeholderWidth="200px" placeholderHeight="200px" src={imageSrc} alt={`Album ${albumName}`} />
                    </figure>
                    <p className="tw-absolute tw-p-3 -tw-bottom-2 -tw-left-2 tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded-full tw-bg-gray-700 tw-text-white tw-font-light itemPosition">
                        {musicIndex + 1}
                    </p>
                </div>

                <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 itemOptions">
                    <div className="tw-flex tw-flex-col itemArtistSlug">
                        {artistSlug ? (
                            <React.Fragment>
                                <Button
                                    to={`/musicas/${musicSlug}+${artistSlug}`}
                                    className="tw-text-slate-900 tw-font-bold tw-inline-flex tw-items-center tw-min-h-6"
                                    title={`Ir para a música ${musicName}`}
                                >
                                    {musicName.substring(0, 15)}
                                    {' '}
                                    {musicName.trim().length > 15 ? '...' : ''}
                                </Button>
                                <Button
                                    to={`/artistas/${artistSlug}`}
                                    className="tw-text-gray-600 tw-text-sm tw-inline-flex tw-items-center tw-min-h-6"
                                    title={`Ir para o artista ${artistName}`}
                                >
                                    {artistName.substring(0, 15)}
                                    {' '}
                                    {artistName.trim().length > 15 ? '...' : ''}
                                </Button>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <div className="tw-text-slate-900 tw-font-bold">
                                    {musicName.substring(0, 15)}
                                    {' '}
                                    {musicName.trim().length > 15 ? '...' : ''}
                                </div>
                                <div className="tw-text-gray-500">
                                    {artistName.substring(0, 15)}
                                    {' '}
                                    {artistName.trim().length > 15 ? '...' : ''}
                                </div>
                            </React.Fragment>
                        )}
                    </div>

                    <div>
                        {!musicTrack ? (
                            <Button className="previewItunes" title={`Sem faixa para ${musicName}`} disabled>
                                <Prohibit size={40} weight="light" />
                            </Button>
                        ) : (
                            <Button
                                className="previewItunes"
                                title={play ? `Pausar ${musicName}` : `Reproduzir ${musicName}`}
                                onClick={(event) => {
                                    event.stopPropagation();

                                    play ? handlingPause() : handlingPlay();
                                }}
                            >
                                {
                                    play ? (
                                        <PauseCircle size={40} />
                                    ) : (
                                        <PlayCircle size={40} />
                                    )
                                }
                            </Button>
                        )}
                    </div>
                </div>

                {showDownloadButton && (
                    <div>
                        <DownloadButton fileType="music" fileId={`${musicSlug}+${artistSlug}`} />
                    </div>
                )}
            </div>
        </article>

    );
};


MusicItem.propTypes = exact({
    currentMusicId: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
    musicIndex: PropTypes.number.isRequired,
    playMediaPlayer: PropTypes.func.isRequired,
    pauseMediaPlayer: PropTypes.func.isRequired,
    setPlaylist: PropTypes.func.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    currentIndex: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
    playerStatus: PropTypes.string.isRequired,
    playlistType: PropTypes.string.isRequired,
    // Injected through withRouters but not used
    /* eslint-disable react/no-unused-prop-types */
    location: PropTypes.object,
    history: PropTypes.object,
    staticContext: PropTypes.object,
    match: PropTypes.object,
    isLive: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    useFilteredMusics: PropTypes.bool,
    playlistData: PropTypes.arrayOf(PropTypes.object).isRequired,
    filteredMusics: PropTypes.arrayOf(PropTypes.object),
    music: PropTypes.shape({
        musicName: PropTypes.string.isRequired,
        musicId: PropTypes.number,
        musicSlug: PropTypes.string,
        musicTrack: PropTypes.string,
        albumName: PropTypes.string,
        albumCover: PropTypes.string,
        itunesCover: PropTypes.string,
        artistName: PropTypes.string.isRequired,
        artistSlug: PropTypes.string,
    }).isRequired,
    showDownloadButton: PropTypes.bool,
});

export default MusicItem;
